/**
 * API client for training and other entities.
 * Uses same base URL and auth as the main app.
 */
import API_BASE_URL from '../config/api';
import {
  isTourDemoActive,
  getDemoTrainingPlans,
  getDemoTrainingLogs,
  getDemoTrainingReminders,
} from '../services/tourDemo';

async function getAuthToken() {
  try {
    const { authClient } = await import('../lib/authClient');
    return await authClient.getAccessToken();
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
}

async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
  const token = await getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const config = { method: options.method || 'GET', headers };
  if (options.body) config.body = JSON.stringify(options.body);

  const response = await fetch(url, config);
  if (response.status === 204) return null;
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(err.error || err.message || `HTTP ${response.status}`);
  }
  return response.json();
}

// Training Plans
const TrainingPlans = {
  getAll() {
    if (isTourDemoActive()) return Promise.resolve(getDemoTrainingPlans());
    return apiRequest('/training/plans');
  },
  getById(id) {
    if (isTourDemoActive() && String(id).startsWith('demo-')) {
      return Promise.resolve(getDemoTrainingPlans().find((p) => p.id === id) || getDemoTrainingPlans()[0]);
    }
    return apiRequest(`/training/plans/${id}`);
  },
  create(body) {
    return apiRequest('/training/plans', { method: 'POST', body });
  },
  update(id, body) {
    return apiRequest(`/training/plans/${id}`, { method: 'PUT', body });
  },
  delete(id) {
    return apiRequest(`/training/plans/${id}`, { method: 'DELETE' });
  },
};

// Training Logs
const TrainingLogs = {
  getAll(limit = 100) {
    if (isTourDemoActive()) return Promise.resolve(getDemoTrainingLogs());
    const q = limit != null ? `?limit=${limit}` : '';
    return apiRequest(`/training/logs${q}`);
  },
};

// Training Reminders (pending by default)
const TrainingReminders = {
  getPending() {
    if (isTourDemoActive()) return Promise.resolve(getDemoTrainingReminders());
    return apiRequest('/training/reminders?status=pending');
  },
  getAll(status) {
    const q = status ? `?status=${status}` : '';
    return apiRequest(`/training/reminders${q}`);
  },
};

export const entities = {
  TrainingPlans,
  TrainingLogs,
  TrainingReminders,
};

export { apiRequest };
